<template>
    <stat-card :expandable="expandable" :expanded="expanded" @toggle:expand="$emit('toggle:expand')">
        <template #header>
            <kimsa-combobox class="hm-metric" dense solo :items="typeOptions" @change="type = $event" :value="type"
                placeholder-class no-object height="30" />
        </template>

        <div class="hm-grid">
            <div class="hm-corner" />
            <div v-for="d in 7" :key="'h' + d" class="hm-col-hdr">{{ $t('days.day_short') }}{{ d }}</div>
            <div class="hm-col-hdr hm-accent">{{ $t('days.total_short') }}</div>

            <template v-for="(week, wi) in weeksData">
                <div :key="'r' + wi" class="hm-row-hdr">{{ $t('days.week_short') }}{{ wi + 1 }}</div>
                <div v-for="(value, di) in week" :key="'c' + wi + '-' + di" class="hm-cell" :class="cellClass(value)">
                    {{ value }}
                </div>
                <div :key="'t' + wi" class="hm-cell tot">{{ totalsPerWeek[wi] }}</div>
            </template>

            <div class="hm-row-hdr hm-accent">{{ $t('days.total_short') }}</div>
            <div v-for="(total, di) in totalsPerDay" :key="'d' + di" class="hm-cell tot-row">{{ total }}</div>
            <div class="hm-cell tot hm-grand">{{ grandTotal }}</div>
        </div>

        <div class="hm-legend">
            <span class="hm-leg-lbl">{{ $t('statistics.less') }}</span>
            <div class="hm-leg-swatches">
                <span class="hm-sw" />
                <span class="hm-sw lo" />
                <span class="hm-sw mid" />
                <span class="hm-sw hi" />
            </div>
            <span class="hm-leg-lbl">{{ $t('statistics.more') }}</span>
        </div>
    </stat-card>
</template>

<script>
import { calculateWellnessPreInfo } from '../../utils/Routine';
import KimsaCombobox from '../common/inputs/KimsaCombobox.vue';
import StatCard from './StatCard.vue';
import { expandableCardMixin } from '@/mixins/expandableCard';

export default {
    components: { KimsaCombobox, StatCard },
    mixins: [expandableCardMixin],
    name: "LastWeeksChart",
    props: {
        docs: {
            type: Array,
            default: () => [],
        },
    },
    data(vm) {
        return {
            typeOptions: [
                { name: vm.$t('statistics.wellness'), value: 'wellness' },
                { name: vm.$t('statistics.charge'), value: 'charge' },
                { name: vm.$t('statistics.sets_total'), value: 'sets_total' },
                { name: vm.$t('statistics.intensity_strength'), value: 'intensity_strength' },
            ],
            type: 'wellness',
            weeksData: [],
            totalsPerWeek: [],
            totalsPerDay: [],
            rpeTable: [
                { name: 10, reps: [100, 95, 91, 87, 85, 83, 81, 79] },
                { name: 9.5, reps: [97, 93, 89, 86, 84, 82, 80, 77.5] },
                { name: 9, reps: [95, 91, 87, 85, 83, 81, 79, 76] },
                { name: 8.5, reps: [93, 89, 86, 84, 82, 80, 77.5, 74.5] },
                { name: 8, reps: [91, 87, 85, 83, 81, 79, 76, 73] },
                { name: 7.5, reps: [89, 86, 84, 82, 80, 77.5, 74.5, 71.5] },
                { name: 7, reps: [87, 85, 83, 81, 79, 76, 73, 70] },
            ],
            rirTable: [
                { name: 0, reps: [100, 95, 91, 87, 85, 83, 81, 79] },
                { name: 1, reps: [95, 91, 87, 85, 83, 81, 79, 76] },
                { name: 2, reps: [91, 87, 85, 83, 81, 79, 76, 73] },
                { name: 3, reps: [89, 86, 84, 82, 80, 77.5, 74.5, 71.5] },
                { name: 4, reps: [87, 85, 83, 81, 79, 76, 73, 70] },
            ],
        }
    },
    computed: {
        maxVal(vm = this) {
            let max = 0
            vm.weeksData.forEach(week => week.forEach(val => {
                const n = Number(val)
                if (Number.isFinite(n) && n > max) max = n
            }))
            return max
        },
        grandTotal(vm = this) {
            const total = vm.totalsPerDay.reduce((acc, val) => acc + (val !== '' ? Number(val) : 0), 0)
            return total !== 0 ? Number(total).toFixed(1).replace('.0', '') : ''
        },
    },
    mounted() {
        const vm = this
        vm.setLastWeekValues(vm.docs)
    },
    methods: {
        setLastWeekValues(docs) {
            const vm = this;
            const values = vm.getLastWeekValues(docs || [])
            vm.weeksData = values.weeksData
            vm.totalsPerWeek = values.totalsPerWeek
            vm.totalsPerDay = values.totalsPerDay
        },
        getLastWeekValues(docs) {
            const vm = this;
            let weeksData = []
            let totalsPerWeek = []
            let totalsPerDay = []
            const endDate = vm.$moment().endOf('week');
            const startDate = vm.$moment(endDate).subtract(3, 'weeks').startOf('week');
            let currentDate = startDate.clone()
            let weeks = [[], [], [], []];
            let currentWeek = 0
            let dayCount = 0

            totalsPerDay = Array(7).fill(0);
            let countPerDay = Array(7).fill(0);

            while (currentDate.isSameOrBefore(endDate)) {
                const currentRoutine = docs.find(routine => routine.rawDate.full === currentDate.format('YYYY-MM-DD')) || null
                const value = Number(vm.getValueByType(currentRoutine));
                weeks[currentWeek].push(value !== 0 ? Number(value).toFixed(1).replace('.0', '') : '');

                if (vm.type === 'intensity_strength') {
                    if (value !== 0) {
                        totalsPerDay[dayCount] += value;
                        countPerDay[dayCount] += 1;
                    }
                } else {
                    totalsPerDay[dayCount] += value;
                }

                currentDate.add(1, 'day');
                dayCount++;
                if (dayCount === 7) {
                    currentWeek++;
                    dayCount = 0;
                }
            }

            weeksData = weeks
            totalsPerWeek = weeks.map(week => {
                const total = week.reduce((acc, val) => acc + (val !== '' ? Number(val) : 0), 0);
                if (vm.type === 'intensity_strength') {
                    const validEntries = week.filter(val => val !== '');
                    const average = validEntries.length > 0 ? total / validEntries.length : 0;
                    return average !== 0 ? Number(average).toFixed(1).replace('.0', '') : '';
                }
                return total !== 0 ? Number(total).toFixed(1).replace('.0', '') : '';
            });


            if (vm.type === 'intensity_strength') {
                totalsPerDay = totalsPerDay.map((total, index) => {
                    return countPerDay[index] > 0 ? (total / countPerDay[index]).toFixed(1).replace('.0', '') : '';
                });
            } else {
                totalsPerDay = totalsPerDay.map(total => (total !== 0 ? total : ''));
            }

            return {
                weeksData,
                totalsPerWeek,
                totalsPerDay,
            };
        },
        getValueByType(routine) {
            const vm = this;
            if (vm.type === 'wellness') {
                const preInfo = routine?.advance?.preInfo || null
                if (!preInfo) return ''
                return calculateWellnessPreInfo(preInfo)
            }
            if (vm.type === 'charge') {
                const postInfo = routine?.advance?.postInfo || null
                if (!postInfo) return ''
                return postInfo.duration * postInfo.effort
            }
            if (vm.type === 'sets_total') {
                let sets_total = 0
                routine?.blocks?.forEach(block => {
                    block.exercises.forEach(exercise => {
                        if (exercise.type !== 'rest') {
                            let sets = Number(exercise.config?.sets?.length || 0)
                            sets_total += sets
                        }
                    })
                })
                return sets_total === 0 ? '' : sets_total
            }
            if (vm.type === 'intensity_strength') {
                let percentage = 0
                let rpe = 0
                let rir = 0
                let quantity_strong = 0
                routine?.blocks?.forEach(block => {
                    block.exercises.forEach(exercise => {
                        if (exercise.type !== 'rest') {
                            exercise.config?.sets?.forEach((set, i) => {
                                if (vm.categoryKey(exercise.categories?.[0]) === vm.CATEGORY.STRONG) {
                                    let valuePercentage = vm.takeValueFromSet(set, 'percentage')
                                    percentage += valuePercentage
                                    let valueRpe = vm.takeValueFromSet(set, 'rpe')
                                    rpe += valueRpe ? vm.getValueFromTable(valueRpe, set, 'rpeTable') : 0
                                    let valueRir = vm.takeValueFromSet(set, 'rir')
                                    rir += valueRir ? vm.getValueFromTable(valueRir, set, 'rirTable') : 0
                                    if (valuePercentage > 0 || valueRir > 0 || valueRpe > 0) quantity_strong++
                                }
                            })
                        }
                    })
                })
                let intensity_strength = percentage + rpe + rir
                if (quantity_strong > 0) intensity_strength /= quantity_strong
                return intensity_strength === 0 ? '' : Number(intensity_strength).toFixed(1).replace('.0', '')
            }
            return ''
        },
        takeValueFromSet(set, attribute) {
            const vm = this
            let defaultValue = 0
            let value = set.data.find(data => data.short_name === `short_${attribute}`)
            return !!value || value === 0 ? Number(value.value || 0) : defaultValue
        },
        getValueFromTable(value, set, typeTable) {
            const vm = this
            if (typeTable === 'rpeTable') {
                if (value > 10) value = 10
                if (value < 7) value = 7
            }
            if (typeTable === 'rirTable') {
                if (value > 4) value = 4
                if (value < 0) value = 0
            }
            let findValueTable = vm[typeTable]?.find(table => table.name == value) || null
            let reps = set.data.find(data => data.short_name === 'short_reps') || null
            let repsValue = reps && (Number(reps.value || 0) - 1) || 0
            if (repsValue > 7) repsValue = 7
            if (repsValue < 0) repsValue = 0
            return findValueTable && repsValue && findValueTable.reps[repsValue] || 0
        },
        cellClass(value) {
            const vm = this
            if (value === '' || value == null) return ''
            const n = Number(value)
            if (!Number.isFinite(n) || n === 0) return ''
            const ratio = vm.maxVal > 0 ? n / vm.maxVal : 0
            if (ratio > 0.75) return 'hi'
            if (ratio > 0.4) return 'mid'
            return 'lo'
        }
    },
    watch: {
        type() {
            this.setLastWeekValues(this.docs)
        },
        docs() {
            this.setLastWeekValues(this.docs)
        },
    }
}
</script>

<style scoped>
.hm-metric {
    flex: 1;
    max-width: 200px;
}

.hm-metric ::v-deep .v-input,
.hm-metric ::v-deep .v-text-field {
    margin: 0;
    padding: 0;
}

.hm-metric ::v-deep .v-input__control,
.hm-metric ::v-deep .v-input__slot {
    min-height: 30px !important;
}

.hm-metric ::v-deep .v-input__slot {
    padding: 0 10px !important;
}

.hm-metric ::v-deep .v-select__selection {
    font-size: 12px;
    font-weight: 600;
    text-transform: none;
    letter-spacing: normal;
    color: var(--v-txtSecondary-base);
}

.hm-metric ::v-deep .v-input__append-inner {
    margin-top: 0;
    align-self: center;
}

.hm-grid {
    flex: 1;
    display: grid;
    grid-template-columns: 24px repeat(8, 1fr);
    grid-auto-rows: 1fr;
    gap: 3px;
    min-height: 0;
}

.hm-col-hdr,
.hm-row-hdr {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 9px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--v-txtMuted-base);
}

.hm-accent {
    color: var(--v-secondary-base);
}

.hm-cell {
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 700;
    background: var(--v-surface2-base);
    color: var(--v-txtMuted-base);
    overflow: hidden;
}

.hm-cell.lo {
    background: rgba(0, 153, 102, 0.4);
    border: 1px solid rgba(0, 153, 102, 0.6);
    color: #fff;
}

.hm-cell.mid {
    background: rgba(0, 153, 102, 0.75);
    border: 1px solid #009966;
    color: #fff;
}

.hm-cell.hi {
    background: #009966;
    border: 1px solid #00cc88;
    color: #fff;
}

.hm-cell.tot {
    background: rgba(51, 153, 153, 0.3);
    border: 1px solid rgba(51, 153, 153, 0.5);
    color: var(--v-secondary-base);
}

.hm-cell.tot-row {
    background: rgba(51, 153, 153, 0.15);
    border: 1px solid rgba(51, 153, 153, 0.3);
    color: var(--v-secondary-base);
}

.hm-cell.hm-grand {
    background: rgba(51, 153, 153, 0.42);
    border: 1px solid rgba(51, 153, 153, 0.7);
}

.hm-legend {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 10px;
    flex-shrink: 0;
}

.hm-leg-lbl {
    font-size: 9px;
    color: var(--v-txtMuted-base);
}

.hm-leg-swatches {
    display: flex;
    gap: 3px;
}

.hm-sw {
    width: 14px;
    height: 14px;
    border-radius: 3px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
}

.hm-sw.lo {
    background: rgba(0, 153, 102, 0.4);
    border-color: rgba(0, 153, 102, 0.6);
}

.hm-sw.mid {
    background: rgba(0, 153, 102, 0.75);
    border-color: #009966;
}

.hm-sw.hi {
    background: #009966;
    border-color: #00cc88;
}
</style>